import { ImageProps } from 'components/commercetools-ui/atoms/image';

/**
 * Where a gallery image came from. The PDP composes its gallery from tiers of descending
 * specificity, and the selected image is remembered by tier rather than by index: index 3
 * may be the shared finish photo for one finish and a product detail shot — or out of range
 * entirely — for the next.
 *
 * - `product` — the SKU's own images, whether SKU+finish tagged or the base gallery.
 * - `species` — `finish_species:<speciesSlug>:<finishKey>`.
 * - `generic` — `finish:<finishKey>`.
 */
export type GalleryTier = 'product' | 'species' | 'generic';

export type GalleryEntry = { image: ImageProps; tier: GalleryTier };

/** A remembered gallery selection: which tier, and how far into that tier. */
export type GalleryPosition = { tier: GalleryTier; offset: number };

export const GALLERY_START: GalleryPosition = { tier: 'product', offset: 0 };

/**
 * Flattens the tiers into the gallery order, dropping images that repeat across tiers (an
 * asset tagged both for the SKU and for the finish) and images with no renderable source.
 * First occurrence wins, so a duplicate keeps its most specific tier.
 */
export const composeGalleryEntries = (tiers: Record<GalleryTier, ImageProps[]>): GalleryEntry[] => {
  const seenSources = new Set<string>();
  const order: GalleryTier[] = ['product', 'species', 'generic'];

  return order.flatMap((tier) =>
    tiers[tier].flatMap((image) => {
      const source = image.src || image.media?.mediaId;
      if (!source || seenSources.has(source)) return [];
      seenSources.add(source);
      return [{ image, tier }];
    }),
  );
};

/**
 * The slide to open on, for a position remembered from a previous finish. Falls back to the
 * start of the tier when the new finish has fewer images in it, and to the first image when
 * the tier is absent entirely — there is nothing to stay on.
 */
export const resolveGalleryIndex = (entries: GalleryEntry[], position: GalleryPosition): number => {
  const tierIndexes = entries.flatMap((entry, index) => (entry.tier === position.tier ? [index] : []));
  if (!tierIndexes.length) return 0;

  return tierIndexes[Math.min(position.offset, tierIndexes.length - 1)];
};

/**
 * A key that changes exactly when the gallery's images change.
 *
 * The gallery is remounted to force a clean Swiper re-init and to re-read `initialSlide`.
 * Keying that on the selected finish is wrong: the finish changes one render before its
 * images arrive, so the gallery would remount against a stale image set and never remount
 * again once the real images landed — stranding the viewer on whatever slide the incomplete
 * set resolved to. Keying on the images themselves ties the remount to the thing Swiper
 * actually cares about, so the seed is always resolved against the images being displayed.
 */
export const galleryImagesKey = (entries: GalleryEntry[]): string =>
  entries.map(({ image }) => image.media?.mediaId ?? image.src).join('|');

/** The position to remember for a slide the viewer just landed on. */
export const describeGalleryIndex = (entries: GalleryEntry[], index: number): GalleryPosition => {
  const entry = entries[index];
  if (!entry) return GALLERY_START;

  return { tier: entry.tier, offset: index - entries.findIndex(({ tier }) => tier === entry.tier) };
};
